/**
 * RenderHistory — slide-over listing previous Creatomate batches (per job / all / mine).
 * Each entry can reopen the finished outputs or restore the full edit for a tweak + re-render.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, History, FolderOpen, PencilLine, Loader2, Film, Sparkles, TrendingDown, BadgeCheck, GitBranch } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KIND_ICON = { sneak_peek: Film, just_listed: Sparkles, price_reduction: TrendingDown, recently_sold: BadgeCheck };

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

function BatchRow({ b, onOpenOutputs, onReopenEdit }) {
  const ready = b.jobs.filter((j) => j.status === "succeeded");
  const title = b.property?.name || b.address || (b.wms_job_id ? `Job ${b.wms_job_id}` : "Untitled edit");
  return (
    <div className="rh-row" data-testid={`rh-row-${b.batch_id}`}>
      <div className="rh-thumbs">
        {b.jobs.slice(0, 4).map((j) => {
          const Icon = KIND_ICON[j.kind] || Film;
          return j.snapshot_url ? (
            <img key={j.job_id} src={j.snapshot_url} alt={j.label} className={`rh-thumb ${j.status}`} title={`${j.label} · ${j.status}`} />
          ) : (
            <span key={j.job_id} className={`rh-thumb rh-thumb-icon ${j.status}`} title={`${j.label} · ${j.status}`}><Icon size={14} /></span>
          );
        })}
      </div>
      <div className="rh-main">
        <div className="rh-title-row">
          <span className="rh-title">{title}</span>
          {b.version > 1 && <span className="rh-version" title="Re-rendered from an earlier edit"><GitBranch size={11} /> v{b.version}</span>}
        </div>
        <div className="rh-meta">
          {fmtWhen(b.created_at)} · {b.created_by_name || "—"}
          {b.wms_job_id ? ` · Job ${b.wms_job_id}` : ""}
          {b.wms_client?.name ? ` · ${b.wms_client.name}` : ""}
          {` · ${b.clip_count || 0} clips`}
        </div>
        <div className="rh-outputs">
          {b.jobs.map((j) => (
            <span key={j.job_id} className={`rh-output ${j.status}`}>{j.label?.replace(" Reel", "")}</span>
          ))}
        </div>
      </div>
      <div className="rh-actions">
        <button type="button" className="rh-btn" onClick={() => onOpenOutputs(b)} disabled={ready.length === 0 && !b.jobs.some((j) => !["succeeded", "failed"].includes(j.status))} data-testid={`rh-open-${b.batch_id}`}>
          <FolderOpen size={14} /> Outputs
        </button>
        <button type="button" className="rh-btn is-primary" onClick={() => onReopenEdit(b)} disabled={!b.has_edit_state} title={b.has_edit_state ? "Restore this edit to tweak and re-render" : "No saved edit for this render"} data-testid={`rh-reopen-${b.batch_id}`}>
          <PencilLine size={14} /> Reopen edit
        </button>
      </div>
    </div>
  );
}

export default function RenderHistory({ open, onClose, wmsJobId, onOpenOutputs, onReopenEdit }) {
  const [scope, setScope] = useState(wmsJobId ? "job" : "all");
  const [batches, setBatches] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) setScope(wmsJobId ? "job" : "all"); }, [open, wmsJobId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const params = { limit: 40 };
    if (scope === "job" && wmsJobId) params.wms_job_id = wmsJobId;
    if (scope === "mine") params.mine = true;
    axios.get(`${API}/video/creatomate/batches`, { params })
      .then((r) => { if (!cancelled) setBatches(r.data.batches || []); })
      .catch((e) => { if (!cancelled) { setBatches([]); toast.error(e.response?.data?.detail || "Couldn't load render history"); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, scope, wmsJobId]);

  if (!open) return null;
  return (
    <div className="rh-backdrop" onClick={onClose} data-testid="history-drawer-backdrop">
      <aside className="rh-drawer" onClick={(e) => e.stopPropagation()} data-testid="history-drawer">
        <div className="rh-head">
          <h3><History size={16} /> Render history</h3>
          <button type="button" className="rh-close" onClick={onClose} aria-label="Close" data-testid="history-close"><X size={16} /></button>
        </div>
        <div className="rh-scopes">
          {wmsJobId && (
            <button type="button" className={`rh-scope ${scope === "job" ? "is-active" : ""}`} onClick={() => setScope("job")} data-testid="history-scope-job">Job {wmsJobId}</button>
          )}
          <button type="button" className={`rh-scope ${scope === "all" ? "is-active" : ""}`} onClick={() => setScope("all")} data-testid="history-scope-all">All renders</button>
          <button type="button" className={`rh-scope ${scope === "mine" ? "is-active" : ""}`} onClick={() => setScope("mine")} data-testid="history-scope-mine">My recent</button>
        </div>
        <div className="rh-list" data-testid="history-list">
          {loading && <div className="rh-empty"><Loader2 size={16} className="spin-icon" /> Loading…</div>}
          {!loading && batches && batches.length === 0 && <div className="rh-empty">No renders yet{scope === "job" ? " for this job" : ""}.</div>}
          {!loading && batches && batches.map((b) => (
            <BatchRow key={b.batch_id} b={b} onOpenOutputs={onOpenOutputs} onReopenEdit={onReopenEdit} />
          ))}
        </div>
      </aside>
    </div>
  );
}
